import { useMemo, useState } from 'react';
import { formatManilaDate } from '@dispatchdash/shared';
import { attendanceApi, type AttendanceLogPunch, type AttendanceLogRow } from '@/api/client';
import { useAsync } from '@/hooks';
import { Layout } from '@/components/Layout';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { internalNavItems } from '@/pages/navItems';
import { formatDate, formatDateTime } from '@/lib/format';
import {
  formatClockTime,
  formatDistance,
  formatShiftLength,
  mapsLink,
  punchBadge,
  shiftYmd,
} from '@/lib/attendanceLog';

function Punch({ punch }: { punch: AttendanceLogPunch | null }) {
  if (!punch) return <span className="text-muted">—</span>;
  const badge = punchBadge(punch);
  const link = mapsLink(punch);
  return (
    <div className="att-punch">
      <span className="att-time" title={formatDateTime(punch.recordedAt)}>{formatClockTime(punch.recordedAt)}</span>
      {badge && <StatusBadge status={badge.status} label={badge.label} size="sm" />}
      {link && (
        <a href={link} target="_blank" rel="noreferrer" className="att-map" onClick={(e) => e.stopPropagation()}>
          Map
          {punch.accuracyM != null ? ` (±${formatDistance(punch.accuracyM)})` : ''}
        </a>
      )}
    </div>
  );
}

const flagged = (p: AttendanceLogPunch | null) => !!p && (p.latitude == null || p.hubGeofenceMatch === false);

/** Internal attendance log: who clocked in/out when, and where (crew can clock in anywhere;
 * away-from-hub and missing locations are flagged here, never blocked). */
export function AttendanceLog() {
  const today = formatManilaDate(new Date());
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [onlyFlagged, setOnlyFlagged] = useState(false);

  const { data, loading, error, reload } = useAsync(() => attendanceApi.log({ from, to }), [from, to]);

  const rows = useMemo(
    () => (data?.rows ?? []).filter((r) => !onlyFlagged || flagged(r.clockIn) || flagged(r.clockOut)),
    [data, onlyFlagged],
  );

  const columns = [
    { key: 'date', header: 'Date', render: (r: AttendanceLogRow) => formatDate(r.date), width: '8rem' },
    { key: 'personName', header: 'Person' },
    {
      key: 'clockIn',
      header: 'Clock in',
      value: (r: AttendanceLogRow) => r.clockIn?.recordedAt ?? '',
      render: (r: AttendanceLogRow) => <Punch punch={r.clockIn} />,
    },
    {
      key: 'clockOut',
      header: 'Clock out',
      value: (r: AttendanceLogRow) => r.clockOut?.recordedAt ?? '',
      render: (r: AttendanceLogRow) =>
        r.clockOut || !r.clockIn ? <Punch punch={r.clockOut} /> : <StatusBadge status="active" label="Still clocked in" size="sm" />,
    },
    {
      key: 'hours',
      header: 'Hours',
      value: (r: AttendanceLogRow) => formatShiftLength(r.clockIn?.recordedAt, r.clockOut?.recordedAt),
      render: (r: AttendanceLogRow) => formatShiftLength(r.clockIn?.recordedAt, r.clockOut?.recordedAt),
    },
  ];

  const setRange = (days: number) => {
    setTo(today);
    setFrom(shiftYmd(today, -days));
  };

  return (
    <Layout navItems={internalNavItems}>
      <div className="page">
        <div className="page-header row-between">
          <div>
            <h1>Attendance</h1>
            <p className="text-muted">
              Clock in and out times, and where the phone was. Crew can clock in anywhere — clock-ins
              away from the hub
              {data?.hub?.radiusM != null ? ` (more than ${formatDistance(data.hub.radiusM)})` : ''} or without a
              location are flagged here.
            </p>
          </div>
          <Button type="button" variant="secondary" onClick={() => reload()}>Refresh</Button>
        </div>

        <div className="att-filters">
          <label>
            From
            <input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} />
          </label>
          <label>
            To
            <input type="date" value={to} min={from} max={today} onChange={(e) => e.target.value && setTo(e.target.value)} />
          </label>
          <Button type="button" variant="secondary" size="sm" onClick={() => setRange(0)}>Today</Button>
          <Button type="button" variant="secondary" size="sm" onClick={() => setRange(6)}>Last 7 days</Button>
          <label className="att-check">
            <input type="checkbox" checked={onlyFlagged} onChange={(e) => setOnlyFlagged(e.target.checked)} />
            Only away from hub / no location
          </label>
        </div>

        <DataTable
          columns={columns}
          data={rows}
          keyField="key"
          loading={loading}
          loadError={error}
          onRetry={() => reload()}
          dense
          searchPlaceholder="Filter by name…"
          emptyMessage={onlyFlagged ? 'Nothing flagged for these dates.' : 'No one clocked in on these dates.'}
        />
      </div>
      <style>{`
        .att-filters { display: flex; flex-wrap: wrap; align-items: flex-end; gap: var(--space-sm) var(--space-md); margin-bottom: var(--space-md); }
        .att-filters label { display: flex; flex-direction: column; gap: 2px; font-size: 0.8125rem; font-weight: 600; }
        .att-filters label.att-check { flex-direction: row; align-items: center; gap: var(--space-xs, 6px); font-weight: 500; }
        .att-punch { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 8px; }
        .att-time { font-variant-numeric: tabular-nums; font-weight: 600; }
        .att-map { font-size: 0.8125rem; }
      `}</style>
    </Layout>
  );
}
